import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Plus,
  Star,
  Copy,
  CheckCircle,
  ClipboardList,
  AlertTriangle,
} from "lucide-react";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { ApiError } from "@/api/client";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import {
  useBootstrapSupplierQuotation,
  useActiveSupplierQuotation,
  useCreateSupplierQuotation,
  useSupplierQuotations,
  useSetActiveSupplierQuotation,
  useUpdateSupplierQuotation,
} from "@/features/supplierQuotation/hooks";
import type { SupplierQuotationStatus } from "@/features/supplierQuotation/types";
import { formatDateBR } from "@/lib/timeAgo";

const STATUS_LABELS: Record<SupplierQuotationStatus, string> = {
  not_started: "Não iniciada",
  in_progress: "Aberta",
  completed: "Concluída",
};

const STATUS_COLORS: Record<SupplierQuotationStatus, string> = {
  not_started: "var(--login-muted)",
  in_progress: "#d97706",
  completed: "#16a34a",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.625rem 0.75rem",
  background: "var(--login-bg)",
  border: "1px solid var(--login-border)",
  borderRadius: "0.5rem",
  color: "var(--login-text)",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.9375rem",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  textTransform: "uppercase",
  fontSize: "0.75rem",
  letterSpacing: "0.06em",
  color: "var(--login-muted)",
};

const btnPrimary: React.CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  background: "var(--login-accent)",
  border: "none",
  borderRadius: "0.5rem",
  color: "#fff",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  textTransform: "uppercase",
  fontSize: "1rem",
  letterSpacing: "0.06em",
  cursor: "pointer",
};

const quickDateOptionStyle: React.CSSProperties = {
  flex: 1,
  minWidth: "120px",
  padding: "0.9rem 1rem",
  borderRadius: "0.75rem",
  border: "1px solid var(--login-border)",
  background: "var(--login-bg)",
  color: "var(--login-text)",
  cursor: "pointer",
  textAlign: "left",
  transition: "transform 0.15s, border-color 0.15s, background 0.15s",
};

const quickDateHintStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.8125rem",
  color: "var(--login-muted)",
  margin: 0,
};

function addDays(base: Date, days: number) {
  const next = new Date(base);
  next.setDate(next.getDate() + days);
  return next;
}

function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toDisplayDate(date: Date) {
  const day = `${date.getDate()}`.padStart(2, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function maskDisplayDate(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function displayToIsoDate(value: string) {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";
  const [, day, month, year] = match;
  const candidate = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    Number.isNaN(candidate.getTime()) ||
    candidate.getFullYear() !== Number(year) ||
    candidate.getMonth() !== Number(month) - 1 ||
    candidate.getDate() !== Number(day)
  ) {
    return "";
  }
  return toIsoDate(candidate);
}

function isoToDisplayDate(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

function StatusBadge({ status }: { status: SupplierQuotationStatus }) {
  return (
    <span
      style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: "0.75rem",
        letterSpacing: "0.06em",
        color: STATUS_COLORS[status],
        background: `${STATUS_COLORS[status]}18`,
        padding: "0.15rem 0.5rem",
        borderRadius: "0.25rem",
        whiteSpace: "nowrap",
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function QuickDateChooser({
  customDateText,
  confirmLabel,
  isPending,
  onCustomDateChange,
  onPickToday,
  onPickTomorrow,
  onConfirm,
  onCancel,
}: {
  customDateText: string;
  confirmLabel: string;
  isPending: boolean;
  onCustomDateChange: (value: string) => void;
  onPickToday: () => void;
  onPickTomorrow: () => void;
  onConfirm: () => void;
  onCancel?: () => void;
}) {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  const todayIso = toIsoDate(today);
  const tomorrowIso = toIsoDate(tomorrow);
  const customDateIso = displayToIsoDate(customDateText);
  const isTodaySelected = customDateIso === todayIso;
  const isTomorrowSelected = customDateIso === tomorrowIso;
  const customInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
        <button
          type="button"
          onClick={onPickToday}
          disabled={isPending}
          style={{
            ...quickDateOptionStyle,
            borderColor: isTodaySelected
              ? "var(--login-accent)"
              : "var(--login-border)",
            background: isTodaySelected
              ? "var(--login-accent)12"
              : "var(--login-bg)",
            boxShadow: isTodaySelected
              ? "0 0 0 1px var(--login-accent) inset"
              : "none",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "1rem",
              letterSpacing: "0.06em",
              color: "var(--login-text)",
            }}
          >
            Hoje
          </p>
          <p style={{ ...quickDateHintStyle, marginTop: "0.2rem" }}>
            {toDisplayDate(today)}
          </p>
        </button>
        <button
          type="button"
          onClick={onPickTomorrow}
          disabled={isPending}
          style={{
            ...quickDateOptionStyle,
            borderColor: isTomorrowSelected
              ? "var(--login-accent)"
              : "var(--login-border)",
            background: isTomorrowSelected
              ? "var(--login-accent)12"
              : "var(--login-bg)",
            boxShadow: isTomorrowSelected
              ? "0 0 0 1px var(--login-accent) inset"
              : "none",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "1rem",
              letterSpacing: "0.06em",
              color: "var(--login-text)",
            }}
          >
            Amanhã
          </p>
          <p style={{ ...quickDateHintStyle, marginTop: "0.2rem" }}>
            {toDisplayDate(tomorrow)}
          </p>
        </button>
      </div>

      <div
        onClick={() => customInputRef.current?.focus()}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.375rem",
          padding: "0.9rem 1rem",
          borderRadius: "0.75rem",
          border: `1px solid ${
            customDateText ? "var(--login-accent)" : "var(--login-border)"
          }`,
          background: customDateText
            ? "var(--login-accent)10"
            : "var(--login-bg)",
          boxShadow: customDateText
            ? "0 0 0 1px var(--login-accent) inset"
            : "none",
          cursor: "text",
        }}
      >
        <label style={labelStyle}>Outra data</label>
        <input
          ref={customInputRef}
          inputMode="numeric"
          placeholder="DD/MM/YYYY"
          value={customDateText}
          onChange={(e) => onCustomDateChange(maskDisplayDate(e.target.value))}
          style={inputStyle}
        />
      </div>

      <div style={{ display: "flex", gap: "0.75rem" }}>
        <button
          type="button"
          className="btn-primary"
          style={btnPrimary}
          onClick={onConfirm}
          disabled={!customDateIso || isPending}
        >
          {confirmLabel}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            style={{
              minWidth: "110px",
              background: "var(--login-border)",
              color: "var(--login-muted)",
              border: "none",
              borderRadius: "0.375rem",
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "0.05em",
              padding: "0.25rem 0.625rem",
              cursor: "pointer",
            }}
          >
            Cancelar
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function SupplierListaPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_auth/lista" });
  const { data, isLoading } = useSupplierQuotations();
  const { data: activeQuotationSetting } = useActiveSupplierQuotation();
  const createMutation = useCreateSupplierQuotation();
  const bootstrapMutation = useBootstrapSupplierQuotation();
  const updateQuotationMutation = useUpdateSupplierQuotation();
  const setActiveMutation = useSetActiveSupplierQuotation();
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [newDateText, setNewDateText] = useState("");
  const [bootstrapSourceId, setBootstrapSourceId] = useState<string | null>(
    null,
  );
  const [bootstrapDateText, setBootstrapDateText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const handledTargetDateRef = useRef<string | null>(null);

  const quotations = data?.items ?? [];

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    const targetDate = search.date;
    if (!targetDate) {
      handledTargetDateRef.current = null;
      return;
    }
    if (isLoading || handledTargetDateRef.current === targetDate) return;

    handledTargetDateRef.current = targetDate;
    const existing = quotations.find(
      (quotation) => quotation.date.slice(0, 10) === targetDate,
    );
    if (existing) {
      navigate({
        to: "/lista/$quotationId",
        params: { quotationId: existing.id },
      });
      return;
    }

    setNewDateText(isoToDisplayDate(targetDate));
    setShowCreateDrawer(true);
  }, [isLoading, navigate, quotations, search.date]);

  async function handleCreate(date: string) {
    if (!date) return;
    setError(null);
    try {
      const created = await createMutation.mutateAsync({ date });
      setShowCreateDrawer(false);
      setNewDateText("");
      navigate({
        to: "/lista/$quotationId",
        params: { quotationId: created.id },
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setShowCreateDrawer(false);
        setNewDateText("");
        setToast("Já existe uma lista para essa data.");
        return;
      }
      setError(
        getUserFacingErrorMessage(
          e,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  async function handleBootstrap(targetDate: string) {
    if (!bootstrapSourceId || !targetDate) return;
    setError(null);
    try {
      const created = await bootstrapMutation.mutateAsync({
        sourceId: bootstrapSourceId,
        payload: { targetDate },
      });
      setBootstrapSourceId(null);
      setBootstrapDateText("");
      navigate({
        to: "/lista/$quotationId",
        params: { quotationId: created.id },
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setBootstrapSourceId(null);
        setBootstrapDateText("");
        setToast("Já existe uma lista para essa data.");
        return;
      }
      setError(
        getUserFacingErrorMessage(
          e,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  async function handleMarkDone(quotationId: string) {
    setError(null);
    try {
      await updateQuotationMutation.mutateAsync({
        quotationId,
        payload: { status: "completed" },
      });
    } catch (e) {
      setError(
        getUserFacingErrorMessage(
          e,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  async function handleSetActive(quotationId: string) {
    setError(null);
    try {
      await setActiveMutation.mutateAsync(quotationId);
    } catch (e) {
      setError(
        getUserFacingErrorMessage(
          e,
          "Não foi possível salvar a lista. Tente novamente.",
        ),
      );
    }
  }

  const activeQuotationId = activeQuotationSetting?.value ?? "";
  const isPending =
    createMutation.isPending ||
    bootstrapMutation.isPending ||
    updateQuotationMutation.isPending ||
    setActiveMutation.isPending;

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes toastIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        .btn-add { transition: opacity 0.15s, transform 0.15s; }
        .btn-add:hover { opacity: 0.85; transform: scale(1.02); }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .btn-primary:hover:not(:disabled) { opacity: 0.85; }
        .q-row { transition: border-color 0.15s; }
        .q-row:hover { border-color: var(--login-accent) !important; }
        .btn-sm { transition: opacity 0.15s; border: none; cursor: pointer; border-radius: 0.375rem; font-family: 'Barlow Condensed', sans-serif; font-weight: 700; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.05em; padding: 0.25rem 0.625rem; display: inline-flex; align-items: center; justify-content: center; gap: 0.25rem; }
        .btn-sm:hover:not(:disabled) { opacity: 0.8; }
        .btn-sm:disabled { opacity: 0.5; cursor: not-allowed; }
        @media (max-width: 400px) {
          .btn-sm { padding: 0.25rem 0.45rem; font-size: 0.65rem; min-width: 32px; min-height: 28px; }
          .hide-sm { display: none; }
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          top: "1.25rem",
          left: "1.25rem",
          right: "1.25rem",
          zIndex: 40,
          pointerEvents: "none",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {toast ? (
          <div
            style={{
              animation: "toastIn 0.18s ease",
              maxWidth: "320px",
              background: "var(--login-surface)",
              border: "1px solid #ef444430",
              borderRadius: "0.875rem",
              color: "var(--login-text)",
              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.14)",
              padding: "0.875rem 1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <div
              style={{
                width: "2rem",
                height: "2rem",
                borderRadius: "999px",
                background: "#ef444418",
                color: "#ef4444",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <AlertTriangle size={16} strokeWidth={2.25} />
            </div>
            <div style={{ minWidth: 0 }}>
              <p
                style={{
                  margin: 0,
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.9rem",
                }}
              >
                Data indisponível
              </p>
              <p
                style={{
                  margin: "0.15rem 0 0",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.875rem",
                  color: "var(--login-muted)",
                }}
              >
                {toast}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: "1.5rem",
          maxWidth: "800px",
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          <h1
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "1.75rem",
              letterSpacing: "0.04em",
              color: "var(--login-text)",
              margin: 0,
            }}
          >
            Lista
          </h1>
          <button
            className="btn-add"
            onClick={() => setShowCreateDrawer(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.375rem",
              padding: "0.5rem 0.875rem",
              background: "var(--login-accent)",
              border: "none",
              borderRadius: "0.5rem",
              color: "#fff",
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.875rem",
              letterSpacing: "0.06em",
              cursor: "pointer",
            }}
          >
            <Plus size={16} strokeWidth={2} />
            Nova Lista
          </button>
        </div>

        {error ? (
          <div
            style={{
              color: "var(--color-error, #ef4444)",
              fontSize: "0.875rem",
              marginBottom: "1rem",
            }}
          >
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: "var(--login-muted)",
              fontSize: "0.875rem",
            }}
          >
            Carregando...
          </p>
        ) : null}

        {!isLoading && quotations.length === 0 ? (
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: "var(--login-muted)",
              fontSize: "0.875rem",
            }}
          >
            Nenhuma lista cadastrada.
          </p>
        ) : null}

        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
        >
          {quotations.map((quotation, index) => {
            const isActive = activeQuotationId === quotation.id;
            return (
              <div
                key={quotation.id}
                style={{
                  animation: "fadeSlideIn 0.18s ease both",
                  animationDelay: `${index * 0.04}s`,
                }}
              >
                <div
                  className="q-row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.875rem 1rem",
                    background: "var(--login-surface)",
                    border: `1px solid ${isActive ? "var(--login-accent)" : "var(--login-border)"}`,
                    borderRadius: "0.5rem",
                  }}
                >
                  <button
                    onClick={() =>
                      navigate({
                        to: "/lista/$quotationId",
                        params: { quotationId: quotation.id },
                      })
                    }
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      padding: 0,
                      minWidth: 0,
                    }}
                  >
                    {isActive ? (
                      <Star
                        size={14}
                        fill="var(--login-accent)"
                        color="var(--login-accent)"
                        style={{ flexShrink: 0 }}
                      />
                    ) : (
                      <ClipboardList
                        size={16}
                        color="var(--login-muted)"
                        style={{ flexShrink: 0 }}
                      />
                    )}
                    <span
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 500,
                        fontSize: "0.9375rem",
                        color: "var(--login-text)",
                      }}
                    >
                      {formatDateBR(quotation.date)}
                    </span>
                    <StatusBadge status={quotation.status} />
                  </button>

                  <div
                    style={{ display: "flex", gap: "0.375rem", flexShrink: 0 }}
                  >
                    {quotation.status === "in_progress" ? (
                      <button
                        className="btn-sm"
                        onClick={() => handleMarkDone(quotation.id)}
                        disabled={isPending}
                        style={{ background: "#16a34a18", color: "#16a34a" }}
                      >
                        <CheckCircle size={12} />
                        Concluir
                      </button>
                    ) : null}
                    {quotation.status === "completed" && !isActive ? (
                      <button
                        className="btn-sm"
                        onClick={() => handleSetActive(quotation.id)}
                        disabled={isPending}
                        style={{
                          background: "var(--login-accent)18",
                          color: "var(--login-accent)",
                        }}
                      >
                        <Star size={12} />
                        Ativar
                      </button>
                    ) : null}
                    <button
                      className="btn-sm"
                      onClick={() => {
                        setBootstrapSourceId(quotation.id);
                        setBootstrapDateText("");
                      }}
                      disabled={isPending}
                      style={{
                        background: "var(--login-border)",
                        color: "var(--login-muted)",
                      }}
                    >
                      <Copy size={12} />
                      <span className="hide-sm">Copiar</span>
                    </button>
                  </div>
                </div>

                {bootstrapSourceId === quotation.id ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.875rem",
                      padding: "0.75rem 1rem",
                      background: "var(--login-surface)",
                      border: "1px solid var(--login-border)",
                      borderTop: "none",
                      borderRadius: "0 0 0.5rem 0.5rem",
                    }}
                  >
                    <span style={labelStyle}>Nova data</span>
                    <QuickDateChooser
                      customDateText={bootstrapDateText}
                      confirmLabel="Copiar"
                      isPending={bootstrapMutation.isPending}
                      onCustomDateChange={setBootstrapDateText}
                      onPickToday={() =>
                        setBootstrapDateText(toDisplayDate(new Date()))
                      }
                      onPickTomorrow={() =>
                        setBootstrapDateText(
                          toDisplayDate(addDays(new Date(), 1)),
                        )
                      }
                      onConfirm={() =>
                        handleBootstrap(displayToIsoDate(bootstrapDateText))
                      }
                      onCancel={() => setBootstrapSourceId(null)}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {quotations.length > 0 && activeQuotationId ? (
          <div
            style={{
              marginTop: "1rem",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              className="btn-sm"
              onClick={() => handleSetActive(null)}
              disabled={isPending}
              style={{
                background: "var(--login-surface)",
                border: "1px solid var(--login-border)",
                color: "var(--login-muted)",
              }}
            >
              Desativar Todas
            </button>
          </div>
        ) : null}

        <AdminDrawer
          open={showCreateDrawer}
          onClose={() => {
            setShowCreateDrawer(false);
            setNewDateText("");
          }}
          title="Nova Lista"
        >
          <QuickDateChooser
            customDateText={newDateText}
            confirmLabel="Criar"
            isPending={createMutation.isPending}
            onCustomDateChange={setNewDateText}
            onPickToday={() => setNewDateText(toDisplayDate(new Date()))}
            onPickTomorrow={() =>
              setNewDateText(toDisplayDate(addDays(new Date(), 1)))
            }
            onConfirm={() => handleCreate(displayToIsoDate(newDateText))}
          />
        </AdminDrawer>
      </div>
    </>
  );
}
